import React, { useMemo, useState, useEffect } from "react";
import Swal from "sweetalert2";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
} from "@mui/material";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import PaymentIcon from "@mui/icons-material/Payment";
import GroupsIcon from "@mui/icons-material/Groups";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import AddIcon from "@mui/icons-material/Add";
import CampaignIcon from "@mui/icons-material/Campaign";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import { motion } from "framer-motion";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import LoadingSpinner from "../../components/LoadingSpinner";
import { fetchMembers, fetchSingleMemberDetails } from "../../services/memberService";
import { fetchMyMemberships } from "../../services/membershipService";
import { getLoans } from "../../services/loanService";
import { fetchTransactionAudit } from "../../services/transactionAuditService";
import {
  fetchGroupTransactions,
  fetchDeskOfficerNotifications,
} from "../../services/deskOfficerService";
import { closeTerm, renewTerm, fetchGroupTerms, fetchGroupTermStatus } from "../../services/groupService";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import Footer from "../Footer";
import { resolveChairpersonDashboardRequests } from "./chairpersonDashboardLoad";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend, Filler);

const brand = "#800b33";
const brandHover = "#6c0a2b";
const WEEK_COUNT = 6;

const formatNumber = (value) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return "0";
  return new Intl.NumberFormat("en-TZ").format(Number(value));
};

const formatTimeAgo = (timestamp, t) => {
  if (!timestamp) return "";
  const created = new Date(timestamp);
  if (Number.isNaN(created.getTime())) return "";
  const diffMs = Date.now() - created.getTime();
  if (diffMs < 0) return t("just now");
  const minute = 60 * 1000;
  const hour = 60 * minute;
  const day = 24 * hour;
  if (diffMs < minute) return t("{{count}} seconds ago", { count: Math.round(diffMs / 1000) });
  if (diffMs < hour) return t("{{count}} minutes ago", { count: Math.round(diffMs / minute) });
  if (diffMs < day) return t("{{count}} hours ago", { count: Math.round(diffMs / hour) });
  return t("{{count}} days ago", { count: Math.round(diffMs / day) });
};

const normalizeListResponse = (payload) => {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload.results)) return payload.results;
  if (Array.isArray(payload.data)) return payload.data;
  return [];
};

const getWeekStart = (date) => {
  const copy = new Date(date);
  const day = copy.getDay(); // 0 (Sun) - 6 (Sat)
  const diff = (day + 6) % 7; // Monday = 0
  copy.setDate(copy.getDate() - diff);
  copy.setHours(0, 0, 0, 0);
  return copy;
};

const buildWeekBuckets = (count) => {
  const buckets = [];
  const today = new Date();
  const anchor = getWeekStart(today);
  for (let offset = count - 1; offset >= 0; offset -= 1) {
    const bucket = new Date(anchor);
    bucket.setDate(anchor.getDate() - offset * 7);
    buckets.push({
      key: bucket.toISOString().slice(0, 10),
      label: bucket.toLocaleDateString("en-TZ", { month: "short", day: "numeric" }),
    });
  }
  return buckets;
};

const buildWeeklySeries = (transactions = [], count = WEEK_COUNT) => {
  const buckets = buildWeekBuckets(count);
  const contributions = buckets.reduce((acc, bucket) => ({ ...acc, [bucket.key]: 0 }), {});
  const disbursements = buckets.reduce((acc, bucket) => ({ ...acc, [bucket.key]: 0 }), {});

  transactions.forEach((tx) => {
    if (!tx?.created_at) return;
    const txDate = new Date(tx.created_at);
    if (Number.isNaN(txDate.getTime())) return;
    const key = getWeekStart(txDate).toISOString().slice(0, 10);
    if (!Object.prototype.hasOwnProperty.call(contributions, key)) return;
    const amount = Math.abs(Number(tx.amount) || 0);
    if (tx.transaction_type === "MEMBER_CONTRIBUTION" || tx.transaction_type === "CONTRIBUTION") {
      contributions[key] += amount;
    }
    if (["LOAN_DISBURSEMENT", "MEMBER_WITHDRAWAL"].includes(tx.transaction_type)) {
      disbursements[key] += amount;
    }
  });

  return {
    labels: buckets.map((bucket) => bucket.label),
    contributions: buckets.map((bucket) => contributions[bucket.key] || 0),
    disbursements: buckets.map((bucket) => disbursements[bucket.key] || 0),
  };
};

const kFormatter = (value) => {
  if (typeof value !== "number") return value;
  return `${Math.round(value / 1000)} K`;
};

const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.08, duration: 0.45, ease: "easeOut" },
  }),
};

const StatCard = ({ icon, label, value, index }) => (
  <motion.div variants={fadeIn} initial="hidden" animate="visible" custom={index}>
    <Card
      sx={{
        p: 2,
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        borderRadius: 3,
        boxShadow: 3,
        "&:hover": { boxShadow: 6, transform: "translateY(-4px)" },
        transition: "all .25s ease",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        <Box
          sx={{
            width: 52,
            height: 52,
            borderRadius: "50%",
            display: "grid",
            placeItems: "center",
            backgroundColor: `${brand}1A`,
          }}
        >
          {icon}
        </Box>
        <Box>
          <Typography variant="body2" color="text.secondary">
            {label}
          </Typography>
          <Typography variant="h6" fontWeight={800}>
            {typeof value === "number" ? formatNumber(value) : value}
          </Typography>
        </Box>
      </Box>
    </Card>
  </motion.div>
);

const ChairpersonDashboard = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleAddMember = () => navigate("/app/chairperson/members");
  const handleViewSummary = () => navigate("/app/desk-officer/groups");
  const handleApproveLoan = () => navigate("/app/loan-approvals");
  const [members, setMembers] = useState([]);
  const [groupTransactions, setGroupTransactions] = useState([]);
  const [transactionStatements, setTransactionStatements] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [balance, setBalance] = useState(0);
  const [nextContribution, setNextContribution] = useState(null);
  const [loans, setLoans] = useState([]);
  const [groupName, setGroupName] = useState("");
  const [termStatus, setTermStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [warning, setWarning] = useState(null);
  const [reloadVersion, setReloadVersion] = useState(0);
  const activeTerm = useMemo(() => termStatus?.current_term || null, [termStatus]);
  const selectedTerm = useMemo(() => activeTerm || termStatus?.last_term || null, [activeTerm, termStatus]);

  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    const loadDashboard = async () => {
      setLoading(true);
      setError(null);
      setWarning(null);
      try {
        const membershipsPayload = await fetchMyMemberships();
        const memberships = normalizeListResponse(membershipsPayload);
        if (memberships.length === 0) {
          throw new Error(t("No active membership found."));
        }
        const activeMembership = memberships.find((m) => m.is_current) || memberships[0];
        setGroupName(activeMembership.group_name || activeMembership.group?.name || "");
        const groupId = activeMembership.group_id || activeMembership.group?.id;
        const status = groupId ? await fetchGroupTermStatus(groupId) : null;
        setTermStatus(status);
        const termId = (status?.current_term || status?.last_term)?.id;

        const requestResults = await Promise.allSettled([
          fetchMembers(),
          fetchTransactionAudit(termId ? { term_id: termId } : {}),
          fetchGroupTransactions({ term_id: termId, signal: controller.signal }),
          getLoans(termId ? { term_id: termId } : {}),
          fetchDeskOfficerNotifications({ signal: controller.signal }),
          fetchSingleMemberDetails(activeMembership.id),
        ]);

        if (!active) return;
        const {
          members: membersList,
          audit: txAuditResponse,
          groupTransactions: groupTxResponse,
          loans: loansResponse,
          notifications: notificationsResponse,
          memberDetails: currentMemberDetails,
          failedSections,
        } = resolveChairpersonDashboardRequests(requestResults);

        setMembers(normalizeListResponse(membersList));
        setTransactionStatements(txAuditResponse?.statements || []);
        const termTransactions = normalizeListResponse(groupTxResponse);
        setGroupTransactions(termTransactions);
        const termBalance = termTransactions.reduce(
          (total, tx) => total + Number(tx.amount || 0),
          0
        );
        setBalance(termBalance);
        setLoans(normalizeListResponse(loansResponse));
        setNotifications(normalizeListResponse(notificationsResponse));
        setNextContribution(currentMemberDetails?.next_due_date || null);
        if (failedSections.length > 0) {
          console.error("ChairpersonDashboard partial load failure:", failedSections);
          setWarning(
            t("Some dashboard sections could not be loaded. You can retry without signing in again."),
          );
        }
        setError(null);
      } catch (err) {
        if (active && err.name !== "AbortError") {
          console.error("ChairpersonDashboard error:", err);
          setError(
            err.status >= 500
              ? t("The dashboard service is temporarily unavailable. Please try again.")
              : err.message || t("Failed to load data"),
          );
        }
      } finally {
        if (active) setLoading(false);
      }
    };

    loadDashboard();
    return () => {
      active = false;
      controller.abort();
    };
  }, [reloadVersion, t]);

  const activeMembers = useMemo(() => members.filter((member) => member.is_active).length, [members]);

  const pendingLoanStatuses = useMemo(
    () => [
      "PENDING_CHAIR_APPROVAL",
      "PENDING_SECRETARY_APPROVAL",
      "PENDING_ACCOUNTANT_APPROVAL",
      "APPROVED_FOR_DISBURSEMENT",
    ],
    []
  );

  const pendingLoans = useMemo(
    () => loans.filter((loan) => pendingLoanStatuses.includes(loan.status)).length,
    [loans, pendingLoanStatuses]
  );

  const stats = useMemo(
    () => [
      { label: t("Active Members"), value: activeMembers, icon: <PeopleAltIcon sx={{ fontSize: 28, color: brand }} /> },
      {
        label: activeTerm
          ? t("Current Term Balance (TZS)")
          : selectedTerm
            ? t("Last Term Balance (TZS)")
            : t("Term Balance (TZS)"),
        value: balance,
        icon: <AccountBalanceWalletIcon sx={{ fontSize: 28, color: brand }} />,
      },
      { label: t("Pending Loans"), value: pendingLoans, icon: <PaymentIcon sx={{ fontSize: 28, color: brand }} /> },
      {
        label: t("Next Contribution"),
        value: nextContribution ? new Date(nextContribution).toLocaleDateString("en-TZ") : t("TBD"),
        icon: <GroupsIcon sx={{ fontSize: 28, color: brand }} />,
      },
    ],
    [activeMembers, activeTerm, balance, pendingLoans, nextContribution, selectedTerm, t]
  );

  const chartSeries = useMemo(() => buildWeeklySeries(groupTransactions), [groupTransactions]);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { position: "bottom" } },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: (value) => kFormatter(value),
        },
      },
      x: { grid: { display: false } },
    },
  };

  const recentContributions = useMemo(() => {
    if (!transactionStatements.length) return [];
    return transactionStatements
      .filter((tx) => tx.transaction_type === "CONTRIBUTION")
      .slice(0, 5)
      .map((tx, idx) => {
        const amount = Number(tx.amount) || 0;
        const df = Number(tx.overpayment_amount) || 0;
        return {
          id: tx.id,
          member: tx.member_name,
          amount,
          df,
          total: amount + df,
          date: tx.created_at,
        };
      });
  }, [transactionStatements]);

  const notificationItems = useMemo(
    () =>
      notifications.map((notification) => {
        const key = notification.id ?? notification.pk ?? `${notification.type}-${notification.created_at}`;
        return {
          id: key,
          text: notification.message || notification.text || "",
          time: formatTimeAgo(notification.created_at, t),
        };
      }),
    [notifications, t]
  );

  const handleManageTerm = async () => {
    try {
      setLoading(true);
      const terms = await fetchGroupTerms();
      const activeTerm = terms.find(t => t.status === "ACTIVE");
      setLoading(false);

      const { value: action } = await Swal.fire({
        title: t("Manage Group Term"),
        text: activeTerm ? t("Current Term: {{name}} (Ends: {{date}})", { name: activeTerm.name, date: activeTerm.end_date }) : t("No Active Term"),
        showDenyButton: true,
        showCancelButton: true,
        confirmButtonText: activeTerm ? t("Close Term") : t("Renew / Start Term"),
        denyButtonText: activeTerm ? t("Renew (Start New)") : t("Cancel"),
        confirmButtonColor: brand,
        denyButtonColor: "#555",
        cancelButtonText: t("Cancel"),
      });

      if (action) {
        // Confirm Button Clicked
        if (activeTerm) {
          // Close Term
          const conf = await Swal.fire({
            title: t("Close Term?"),
            text: t("Are you sure you want to close {{name}}? Balances will be snapshotted.", { name: activeTerm.name }),
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: brand
          });
          if (conf.isConfirmed) {
            await closeTerm(activeTerm.id);
            Swal.fire("Success", "Term Closed", "success");
          }
        } else {
          // No active term, treat confirm as Renew/Start
          performRenewal();
        }
      } else if (Swal.getDenyButton().getAttribute('class').includes('swal2-deny') && activeTerm) {
        // Deny Button Clicked (Renew while active? - logic says Renew creates NEW term)
        // Backend prevents renewal if active term exists.
        // So if active term exists, "Renew" should probably fail or warn.
        Swal.fire("Error", "You must close the active term before renewing.", "error");
      }
    } catch (e) {
      setLoading(false);
      Swal.fire("Error", e.message, "error");
    }
  };

  const performRenewal = async () => {
    const { value: formValues } = await Swal.fire({
      title: t("Start New Term"),
      html:
        `<label>Start Date</label><input id="swal-start" type="date" class="swal2-input">` +
        `<label>End Date</label><input id="swal-end" type="date" class="swal2-input">`,
      focusConfirm: false,
      preConfirm: () => {
        return [
          document.getElementById('swal-start').value,
          document.getElementById('swal-end').value
        ]
      }
    });

    if (formValues) {
      const [start, end] = formValues;
      if (!start || !end) {
        Swal.fire("Error", "Dates are required", "warning");
        return;
      }
      try {
        await renewTerm({ start_date: start, end_date: end });
        Swal.fire("Success", "New Term Started", "success");
      } catch (e) {
        Swal.fire("Error", e.message, "error");
      }
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 900, color: brand }}>
          {t("Chairperson Dashboard")}
        </Typography>
        <Typography variant="subtitle2" color="text.secondary">
          {groupName || t("Your Group")}
        </Typography>
      </Box>

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <LoadingSpinner />
        </Box>
      )}

      {error && !loading && (
        <Alert
          severity="error"
          sx={{ mb: 3 }}
          action={
            <Button color="inherit" size="small" onClick={() => setReloadVersion((value) => value + 1)}>
              {t("Retry")}
            </Button>
          }
        >
          {error || t("Failed to load data")}
        </Alert>
      )}

      {warning && !loading && !error && (
        <Alert
          severity="warning"
          sx={{ mb: 3 }}
          action={
            <Button color="inherit" size="small" onClick={() => setReloadVersion((value) => value + 1)}>
              {t("Retry")}
            </Button>
          }
        >
          {warning}
        </Alert>
      )}

      {!loading && !error && (
        <>
          <Grid container spacing={3} sx={{ mb: 3 }}>
            {stats.map((stat, idx) => (
              <Grid item xs={12} sm={6} md={3} key={stat.label}>
                <StatCard icon={stat.icon} label={stat.label} value={stat.value} index={idx} />
              </Grid>
            ))}
          </Grid>

          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <motion.div variants={fadeIn} initial="hidden" animate="visible" custom={1}>
                <Card sx={{ p: 2, borderRadius: 3, boxShadow: 3 }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1 }}>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        {t("Weekly Contributions (TZS, in K)")}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t("Last 6 weeks")}
                      </Typography>
                    </Box>
                    <Chip
                      label={t("Up to date")}
                      size="small"
                      sx={{
                        fontWeight: 700,
                        bgcolor: "success.main",
                        color: "#fff",
                      }}
                    />
                  </Box>
                  <Divider sx={{ mb: 2 }} />
                  <Box sx={{ height: 300 }}>
                    {chartSeries.labels.length > 0 ? (
                      <Line
                        data={{
                          labels: chartSeries.labels,
                          datasets: [
                            {
                              label: t("contributions"),
                              data: chartSeries.contributions,
                              borderColor: brand,
                              backgroundColor: `${brand}24`,
                              fill: true,
                              tension: 0.35,
                              pointRadius: 3,
                            },
                            {
                              label: t("Disbursements"),
                              data: chartSeries.disbursements,
                              borderColor: "#ff8f00",
                              backgroundColor: "rgba(255,143,0,.15)",
                              fill: true,
                              tension: 0.35,
                              pointRadius: 3,
                            },
                          ],
                        }}
                        options={chartOptions}
                      />
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                        {t("Not enough transaction data yet.")}
                      </Typography>
                    )}
                  </Box>
                </Card>
              </motion.div>
            </Grid>

            <Grid item xs={12} md={4}>
              <motion.div variants={fadeIn} initial="hidden" animate="visible" custom={1.2}>
                <Card sx={{ p: 2, borderRadius: 3, boxShadow: 3 }}>
                  <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 700 }}>
                    {t("Quick Actions")}
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      sx={{ bgcolor: brand, "&:hover": { bgcolor: brandHover } }}
                      onClick={handleAddMember}
                    >
                      {t("Add Member")}
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<CampaignIcon />}
                      sx={{ borderColor: brand, color: brand, "&:hover": { borderColor: brandHover, color: brandHover } }}
                      onClick={handleViewSummary}
                    >
                      {t("View Group Summary")}
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<DoneAllIcon />}
                      sx={{ borderColor: brand, color: brand, "&:hover": { borderColor: brandHover, color: brandHover } }}
                      onClick={handleApproveLoan}
                    >
                      {t("Approve Loan")}
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<AutorenewIcon />} // Need to import AutorenewIcon
                      sx={{ borderColor: brand, color: brand, "&:hover": { borderColor: brandHover, color: brandHover } }}
                      onClick={handleManageTerm}
                    >
                      {t("Manage Term")}
                    </Button>
                  </Box>
                </Card>
              </motion.div>
            </Grid>
          </Grid>

          <Grid container spacing={3} sx={{ mt: 0 }}>
            <Grid item xs={12} md={7}>
              <motion.div variants={fadeIn} initial="hidden" animate="visible" custom={1.4}>
                <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
                  <CardContent>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        {t("Recent Member Contributions (Last 5)")}
                      </Typography>
                    </Box>
                    <TableContainer component={Paper} elevation={0}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>#</TableCell>
                            <TableCell>{t("Member")}</TableCell>
                            <TableCell>{t("Amount")}</TableCell>
                            <TableCell>{t("System Service Charge (SSC)")}</TableCell>
                            <TableCell>{t("Total")}</TableCell>
                            <TableCell>{t("Date")}</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {recentContributions.length > 0 ? (
                            recentContributions.map((contribution, idx) => (
                              <TableRow key={contribution.id} hover>
                                <TableCell>{idx + 1}</TableCell>
                                <TableCell>{contribution.member}</TableCell>
                                <TableCell>{formatNumber(contribution.amount)}</TableCell>
                                <TableCell>{formatNumber(contribution.df)}</TableCell>
                                <TableCell>{formatNumber(contribution.total)}</TableCell>
                                <TableCell>
                                  {contribution.date ? new Date(contribution.date).toLocaleDateString("en-TZ") : "-"}
                                </TableCell>
                              </TableRow>
                            ))
                          ) : (
                            <TableRow>
                              <TableCell colSpan={6} align="center">
                                {t("No contributions recorded yet.")}
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>

            <Grid item xs={12} md={5}>
              <motion.div variants={fadeIn} initial="hidden" animate="visible" custom={1.6}>
                <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 700, color: brand }}>
                      {t("layout.notificationsTitle")}
                    </Typography>
                    {notificationItems.length > 0 ? (
                      notificationItems.map((notification) => (
                        <motion.div
                          key={notification.id}
                          whileHover={{ scale: 1.02 }}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            padding: "12px",
                            borderRadius: "10px",
                            background: "#f9fafb",
                            marginBottom: "10px",
                            border: "1px solid #f0f0f0",
                          }}
                        >
                          <NotificationsActiveIcon sx={{ color: brand, marginRight: 12 }} />
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {notification.text}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {notification.time}
                            </Typography>
                          </Box>
                        </motion.div>
                      ))
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", py: 2 }}>
                        {t("layout.noNotifications")}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          </Grid>
        </>
      )}

      <Footer />
    </Box>
  );
};

export default ChairpersonDashboard;
